import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  Card,
  ErrorState,
  Field,
  PageHeader,
  Select,
  TextField,
} from "../components/ui";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function Settings() {
  const { currentOrg, refetch } = useOrg();
  const qc = useQueryClient();

  const [form, setForm] = useState({
    name: "",
    orgCustomerId: "",
    baseCurrency: "USD",
    fiscalYearStartMonth: 1,
  });

  useEffect(() => {
    if (currentOrg) {
      setForm((f) => ({
        ...f,
        name: currentOrg.name,
        orgCustomerId: currentOrg.orgCustomerId ?? "",
        baseCurrency: currentOrg.baseCurrency,
      }));
    }
  }, [currentOrg]);

  // PATCH /orgs/:id — see note in the scaffold README; updates org metadata
  // including the external org_customer_id link.
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/orgs/${currentOrg!.id}`, {
        name: form.name,
        orgCustomerId: form.orgCustomerId || null,
        baseCurrency: form.baseCurrency,
        fiscalYearStartMonth: Number(form.fiscalYearStartMonth),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["session"] });
      refetch();
    },
  });

  if (!currentOrg) return null;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Organization profile & integrations" />
      <Card className="max-w-2xl p-6">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field required label="Organization name">
            <TextField
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>

          <Field
            label="External org_customer_id"
            hint="64-bit identifier used by your other system (gl.cynch.me integration). Stored exactly as entered."
          >
            <TextField
              inputMode="numeric"
              value={form.orgCustomerId}
              onChange={(e) =>
                setForm({
                  ...form,
                  orgCustomerId: e.target.value.replace(/[^0-9]/g, ""),
                })
              }
              placeholder="e.g. 90071992547409920"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field required label="Base currency">
              <Select
                value={form.baseCurrency}
                onChange={(e) =>
                  setForm({ ...form, baseCurrency: e.target.value })
                }
              >
                {["USD", "EUR", "GBP", "CAD", "AUD"].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </Field>
            <Field required label="Fiscal year starts">
              <Select
                value={form.fiscalYearStartMonth}
                onChange={(e) =>
                  setForm({
                    ...form,
                    fiscalYearStartMonth: Number(e.target.value),
                  })
                }
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
            Organization id: <span className="font-mono">{currentOrg.id}</span> ·
            Your role: <span className="capitalize">{currentOrg.role}</span>
          </div>

          {save.error && <ErrorState error={save.error} />}
          {save.isSuccess && (
            <p className="text-sm text-brand-600">Saved.</p>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
